import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, KpiCard, Spinner } from '@kss/ui';
import {
  AlertTriangle, ChevronRight, FileText, IndianRupee, RefreshCw, Receipt, Wallet,
} from 'lucide-react';
import { pickList, portalApi } from './portalApi';
import { usePortalSession } from './portalSession';

/**
 * Customer-portal landing.
 *
 * Greets the customer by name, shows the headline KPIs they care about
 * (outstanding · overdue · invoices YTD · last payment) and a list of
 * the 5 most-recent invoices with a "Pay outstanding" CTA.
 */

interface InvoiceRow {
  id?: number | string;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  totalAmount?: number;
  balanceAmount?: number;
  status?: string;
}

interface PaymentRow {
  id?: number | string;
  salesPaymentId?: string;
  paymentDate?: string;
  amount?: number;
  paymentMode?: string;
  reference?: string;
}

export default function PortalDashboard() {
  const session = usePortalSession();
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [inv, pay] = await Promise.all([
        portalApi.get<unknown>('/sales/SalesInvoice', { query: { customerId: session?.customerId } }).catch(() => []),
        portalApi.get<unknown>('/sales/SalesPayment', { query: { customerId: session?.customerId } }).catch(() => []),
      ]);
      setInvoices(pickList<InvoiceRow>(inv));
      setPayments(pickList<PaymentRow>(pay));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load your account');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const kpis = useMemo(() => {
    const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
    const outstanding = invoices
      .filter((i) => isOpen(i.status))
      .reduce((s, i) => s + Number(i.balanceAmount ?? i.totalAmount ?? 0), 0);
    const overdue = invoices.filter((i) => {
      if (!isOpen(i.status) || !i.dueDate) return false;
      const t = Date.parse(String(i.dueDate));
      return !Number.isNaN(t) && t < Date.now();
    }).length;
    const invoicesYtd = invoices.filter((i) => {
      const t = Date.parse(String(i.invoiceDate ?? ''));
      return !Number.isNaN(t) && t >= yearStart;
    }).length;
    const lastPayment = payments
      .map((p) => Date.parse(String(p.paymentDate ?? '')))
      .filter((t) => !Number.isNaN(t))
      .sort((a, b) => b - a)[0];
    return {
      outstanding,
      overdue,
      invoicesYtd,
      lastPayment: lastPayment ? new Date(lastPayment) : null,
    };
  }, [invoices, payments]);

  const recent = useMemo(
    () =>
      [...invoices]
        .sort((a, b) => Date.parse(String(b.invoiceDate ?? '')) - Date.parse(String(a.invoiceDate ?? '')))
        .slice(0, 5),
    [invoices],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Hello {session?.customerName ?? 'there'},</h2>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Here&rsquo;s a quick view of your account with this supplier.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {err && <Alert variant="warning">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Outstanding"
          value={`₹ ${inr(kpis.outstanding)}`}
          subtitle={kpis.outstanding > 0 ? 'Pay anytime' : 'Nothing due'}
          icon={<IndianRupee className={`h-5 w-5 ${kpis.outstanding ? 'text-warning' : 'text-success'}`} />}
        />
        <KpiCard
          title="Overdue invoices"
          value={kpis.overdue.toLocaleString()}
          subtitle={kpis.overdue ? 'Past due date' : 'On schedule'}
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.overdue ? 'text-danger' : 'text-success'}`} />}
        />
        <KpiCard
          title="Invoices this year"
          value={kpis.invoicesYtd.toLocaleString()}
          subtitle="Since 1 Jan"
          icon={<FileText className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Last payment"
          value={kpis.lastPayment ? kpis.lastPayment.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
          subtitle={kpis.lastPayment ? relativeTime(kpis.lastPayment.toISOString()) : 'No history'}
          icon={<Wallet className="h-5 w-5 text-primary" />}
        />
      </div>

      {kpis.outstanding > 0 && (
        <Alert variant="warning">
          <div className="flex items-center justify-between gap-3">
            <span>
              You have <strong>₹ {inr(kpis.outstanding)}</strong> outstanding across {invoices.filter((i) => isOpen(i.status)).length} invoice(s).
            </span>
            <Link
              to="/portal/invoices"
              className="inline-flex items-center gap-1 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white"
            >
              Pay now <ChevronRight size={12} />
            </Link>
          </div>
        </Alert>
      )}

      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Recent invoices</h3>
          <Link to="/portal/invoices" className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline">
            See all <ChevronRight size={12} />
          </Link>
        </div>
        {loading && recent.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-[var(--color-text-muted)]">
            <Spinner /> Loading…
          </div>
        ) : recent.length === 0 ? (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">
            No invoices yet. Your supplier will share invoices here as soon as they raise them.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {recent.map((r) => {
              const id = String(r.invoiceId ?? r.id ?? '');
              const open = isOpen(r.status);
              return (
                <li key={id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="grid h-8 w-8 place-items-center rounded-md border border-[var(--color-border)] text-[var(--color-text-muted)]">
                    <Receipt size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{String(r.invoiceNumber ?? id)}</div>
                    <div className="text-[11px] text-[var(--color-text-muted)]">
                      {fmtDate(r.invoiceDate)}
                      {r.dueDate ? ` · due ${fmtDate(r.dueDate)}` : ''}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums">₹ {inr(Number(r.totalAmount ?? 0))}</div>
                    <StatusPill v={String(r.status ?? '')} />
                  </div>
                  {open && (
                    <Link
                      to={`/portal/invoices?focus=${encodeURIComponent(id)}`}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      Pay
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

function StatusPill({ v }: { v: string }) {
  const s = v.toLowerCase();
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (s === 'paid') cls = 'border-success/40 bg-success/10 text-success';
  else if (s === 'overdue') cls = 'border-danger/40 bg-danger/10 text-danger';
  else if (s === 'draft') cls = 'border-warning/40 bg-warning/10 text-warning';
  else if (s === 'posted') cls = 'border-info/40 bg-info/10 text-info';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] capitalize ${cls}`}>
      {v || '—'}
    </span>
  );
}

function isOpen(status: unknown): boolean {
  const s = String(status ?? '').toLowerCase();
  return s !== 'paid' && s !== 'cancelled' && s !== '';
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Math.round((Date.now() - t) / 1000);
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
